import { apiClient } from "@/services/apiClient";

export type TeacherFilterOption = {
  value: string;
  label: string;
  /**
   * Category-only: when true the category is grouped under the filter's "Other"
   * drill-in instead of listed directly. Absent for language/gender options.
   */
  showInOther?: boolean;
};

export type TeacherLanguage = {
  id: number;
  code: string;
  name: string;
};

/**
 * A region a teacher is assigned to. Part of the teacher's AVAILABILITY only —
 * teacher compensation rates are never sent to the frontend.
 */
export type TeacherRegion = {
  id: number;
  name: string;
  slug: string;
};

export type TeacherListUser = {
  id: number;
  image: string;
  name: string;
  slug: string;
  date: string;
  country: string;
  countryCode: string;
  /** Primary language, kept for callers that only need one. */
  language: string;
  languageCode: string;
  /** Every language the teacher instructs in, localized by the `locale` param. */
  languages: TeacherLanguage[];
  /**
   * Availability: explicit Global coverage. Independent of `regions` — a
   * teacher can be Global, region-specific, or Global and region-specific.
   * Optional so older/legacy responses without the field still parse.
   */
  isGlobal?: boolean;
  /** Availability: specific regions assigned to the teacher. */
  regions?: TeacherRegion[];
};

export type TeacherListPage = {
  teachers: TeacherListUser[];
  /** Page metadata, or null when the endpoint returned the flat legacy array. */
  meta: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
    hasMore: boolean;
  } | null;
};

export type TeacherDetailCourse = {
  id: number;
  name: string;
  slug: string;
  description: string;
  image: string | null;
  hourPrice: number;
  category: {
    id: number;
    name: string;
    slug: string;
  } | null;
};

export type TeacherDetail = {
  id: number;
  image: string;
  name: string;
  englishName: string;
  arabicName: string;
  slug: string;
  date: string;
  country: {
    code: string;
    name: string;
  } | null;
  language: {
    code: string;
    name: string;
  } | null;
  gender: string;
  timezone: string;
  bio: string;
  courseTags: string[];
  features: {
    canTeachChildren: boolean;
    canManageLearningDifficulties: boolean;
    freeEvaluationEnabled: boolean;
  };
  languages: Array<{
    id: number;
    code: string;
    name: string;
  }>;
  ijazas: Array<{
    id: number;
    name: string;
  }>;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
    image: string | null;
  }>;
  courses: TeacherDetailCourse[];
  bioHighlights: Array<{
    heading: string;
    items: string[];
  }>;
  /** Availability: explicit Global coverage (independent of `regions`). */
  isGlobal?: boolean;
  /** Availability: specific regions assigned to the teacher. */
  regions?: TeacherRegion[];
};

type CategoryResponseItem = {
  id: number;
  name: string;
  show_in_other?: boolean;
};

type LanguageResponseItem = {
  code: string;
  name: string;
};

type TeacherResponseItem = {
  id: number;
  image: string | null;
  name: string;
  slug: string;
  date: string | null;
  country: string;
  country_code: string | null;
  language: string;
  language_code: string | null;
  is_global?: boolean;
  regions?: TeacherRegion[];
};

type TeacherDetailResponse = {
  id: number;
  image: string | null;
  name: string;
  english_name: string;
  arabic_name: string;
  slug: string;
  date: string | null;
  country: {
    code: string;
    name: string;
  } | null;
  language: {
    code: string;
    name: string;
  } | null;
  gender: string | null;
  timezone: string | null;
  bio: string | null;
  course_tags: string[];
  features: {
    can_teach_children: boolean;
    can_manage_learning_difficulties: boolean;
    free_evaluation_enabled: boolean;
  };
  languages: Array<{
    id: number;
    code: string;
    name: string;
  }>;
  ijazas?: Array<{
    id: number;
    name: string;
  }>;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
    image: string | null;
  }>;
  courses: Array<{
    id: number;
    name: string;
    slug: string;
    description: string | null;
    image: string | null;
    hour_price: number;
    category: {
      id: number;
      name: string;
      slug: string;
    } | null;
  }>;
  bio_highlights?: Array<{
    heading: string;
    items: string[];
  }>;
  is_global?: boolean;
  regions?: TeacherRegion[];
};

const PUBLIC_API_CONFIG = {
  skipAuthRefresh: true,
} as const;

export async function getTeacherFilterOptionsApi(locale: string): Promise<{
  categories: TeacherFilterOption[];
  languages: TeacherFilterOption[];
}> {
  const [categoriesResponse, languagesResponse] = await Promise.all([
    apiClient.get(`/api/categories?locale=${encodeURIComponent(locale)}`, PUBLIC_API_CONFIG),
    apiClient.get(`/api/languages?locale=${encodeURIComponent(locale)}`, PUBLIC_API_CONFIG),
  ]);

  return {
    categories: normalizeCategories(categoriesResponse.data),
    languages: normalizeLanguages(languagesResponse.data),
  };
}

export async function getTeachersApi(
  locale: string,
  query: {
    categoryId?: string;
    languageCode?: string;
    gender?: string;
    regionSlug?: string;
    search?: string;
    page?: number;
    perPage?: number;
  } = {}
): Promise<TeacherListPage> {
  const response = await apiClient.get("/api/teachers", {
    ...PUBLIC_API_CONFIG,
    params: {
      locale,
      category_id: query.categoryId || undefined,
      language_code: query.languageCode || undefined,
      gender: query.gender || undefined,
      region_slug: query.regionSlug || undefined,
      search: query.search?.trim() || undefined,
      // `per_page` is what switches the endpoint from a flat array to the
      // `{ data, meta }` envelope, so it must be sent for a paged request.
      per_page: query.perPage,
      page: query.perPage ? query.page : undefined,
    },
  });

  return normalizeTeacherPage(response.data);
}

export async function getTeacherApi(locale: string, slug: string): Promise<TeacherDetail> {
  const response = await apiClient.get(`/api/teachers/${encodeURIComponent(slug)}`, {
    ...PUBLIC_API_CONFIG,
    params: { locale },
    headers: {
      "Accept-Language": locale,
    },
  });

  return normalizeTeacher(response.data);
}

function normalizeCategories(payload: unknown): TeacherFilterOption[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid categories response");
  }

  return payload
    .filter(isCategoryResponseItem)
    .map((category) => ({
      value: String(category.id),
      label: category.name,
      showInOther: category.show_in_other === true,
    }));
}

function normalizeLanguages(payload: unknown): TeacherFilterOption[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid languages response");
  }

  return payload
    .filter(isLanguageResponseItem)
    .map((language) => ({
      value: language.code,
      label: language.name,
    }));
}

/**
 * `/api/teachers` answers in two shapes: a flat array when `per_page` is
 * omitted (the original contract, still used by any caller that wants
 * everything), and a `{ data, meta }` envelope when it is sent. Both are
 * accepted here so the endpoint can be called either way.
 */
function normalizeTeacherPage(payload: unknown): TeacherListPage {
  if (Array.isArray(payload)) {
    return { teachers: normalizeTeachers(payload), meta: null };
  }

  if (!isRecord(payload) || !Array.isArray(payload.data)) {
    throw new Error("Invalid teachers response");
  }

  const meta = payload.meta;

  return {
    teachers: normalizeTeachers(payload.data),
    meta: isRecord(meta)
      ? {
          currentPage: toPositiveInt(meta.current_page, 1),
          perPage: toPositiveInt(meta.per_page, 12),
          total: toPositiveInt(meta.total, 0),
          lastPage: toPositiveInt(meta.last_page, 1),
          hasMore: meta.has_more === true,
        }
      : null,
  };
}

function toPositiveInt(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number(value);

  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : fallback;
}

function normalizeTeachers(payload: unknown): TeacherListUser[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid teachers response");
  }

  return payload.filter(isTeacherResponseItem).map((teacher) => ({
    id: teacher.id,
    // No default-avatar substitution: an empty image lets TeacherAvatar render
    // its name-keyed initials (its designed fallback). The old
    // "/f/images/default_avatar_2.png" is a Next public asset, but this value
    // flows through resolveOptimizedImageUrl(), which rebases it onto the
    // backend host where that path 404s - breaking the optimizer request.
    image: teacher.image ?? "",
    name: teacher.name.trim(),
    slug: teacher.slug,
    date: teacher.date ?? "",
    country: teacher.country.trim(),
    countryCode: teacher.country_code ?? "",
    language: teacher.language.trim(),
    languageCode: teacher.language_code ?? "",
    // Optional on purpose: older responses (and the flat legacy array) carry
    // only the single `language`, so fall back to that rather than rendering
    // nothing.
    languages: normalizeTeacherLanguages(teacher),
    // Availability only — never compensation. Optional so older responses
    // without the fields still parse.
    isGlobal: teacher.is_global,
    regions: teacher.regions,
  }));
}

function normalizeTeacherLanguages(teacher: TeacherResponseItem): TeacherLanguage[] {
  const raw = (teacher as { languages?: unknown }).languages;

  if (Array.isArray(raw)) {
    const parsed = raw.filter(isTeacherLanguage);

    if (parsed.length > 0) {
      return parsed.map((language) => ({
        id: language.id,
        code: language.code,
        name: language.name.trim(),
      }));
    }
  }

  const fallback = teacher.language.trim();

  return fallback
    ? [{ id: -1, code: teacher.language_code ?? "", name: fallback }]
    : [];
}

function isTeacherLanguage(value: unknown): value is TeacherLanguage {
  return (
    isRecord(value) &&
    typeof value.id === "number" &&
    typeof value.code === "string" &&
    isNonEmptyString(value.name)
  );
}

function normalizeTeacher(payload: unknown): TeacherDetail {
  if (!isTeacherDetailResponse(payload)) {
    throw new Error("Invalid teacher response");
  }

  return {
    id: payload.id,
    // See normalizeTeachers: empty (not the default-avatar public asset) so
    // TeacherAvatar shows initials rather than issuing a doomed optimizer fetch.
    image: payload.image ?? "",
    name: payload.name.trim(),
    englishName: payload.english_name.trim(),
    arabicName: payload.arabic_name.trim(),
    slug: payload.slug,
    date: payload.date ?? "",
    country: payload.country,
    language: payload.language,
    gender: payload.gender ?? "",
    timezone: payload.timezone ?? "",
    bio: payload.bio ?? "",
    courseTags: payload.course_tags,
    features: {
      canTeachChildren: payload.features.can_teach_children,
      canManageLearningDifficulties: payload.features.can_manage_learning_difficulties,
      freeEvaluationEnabled: payload.features.free_evaluation_enabled,
    },
    languages: payload.languages,
    ijazas: payload.ijazas || [],
    categories: payload.categories,
    courses: payload.courses.map((course) => ({
      id: course.id,
      name: course.name,
      slug: course.slug,
      description: course.description ?? "",
      image: course.image,
      hourPrice: course.hour_price,
      category: course.category,
    })),
    bioHighlights: payload.bio_highlights || [],
    // Availability only — never compensation.
    isGlobal: payload.is_global,
    regions: payload.regions,
  };
}

function isCategoryResponseItem(value: unknown): value is CategoryResponseItem {
  return isRecord(value) && typeof value.id === "number" && isNonEmptyString(value.name);
}

function isLanguageResponseItem(value: unknown): value is LanguageResponseItem {
  return isRecord(value) && isNonEmptyString(value.code) && isNonEmptyString(value.name);
}

function isTeacherResponseItem(value: unknown): value is TeacherResponseItem {
  return (
    isRecord(value) &&
    typeof value.id === "number" &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.slug) &&
    (value.image === null || typeof value.image === "string") &&
    (value.date === null || typeof value.date === "string") &&
    typeof value.country === "string" &&
    (value.country_code === null || typeof value.country_code === "string") &&
    typeof value.language === "string" &&
    (value.language_code === null || typeof value.language_code === "string")
  );
}

function isTeacherDetailResponse(value: unknown): value is TeacherDetailResponse {
  return (
    isRecord(value) &&
    typeof value.id === "number" &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.english_name) &&
    isNonEmptyString(value.arabic_name) &&
    isNonEmptyString(value.slug) &&
    (value.image === null || typeof value.image === "string") &&
    (value.date === null || typeof value.date === "string") &&
    (value.country === null || isNamedCodeRecord(value.country)) &&
    (value.language === null || isNamedCodeRecord(value.language)) &&
    (value.gender === null || typeof value.gender === "string") &&
    (value.timezone === null || typeof value.timezone === "string") &&
    (value.bio === null || typeof value.bio === "string") &&
    Array.isArray(value.course_tags) &&
    value.course_tags.every((item) => typeof item === "string") &&
    isRecord(value.features) &&
    typeof value.features.can_teach_children === "boolean" &&
    typeof value.features.can_manage_learning_difficulties === "boolean" &&
    typeof value.features.free_evaluation_enabled === "boolean" &&
    Array.isArray(value.languages) &&
    (!("ijazas" in value) || Array.isArray(value.ijazas)) &&
    Array.isArray(value.categories) &&
    Array.isArray(value.courses) &&
    (!("bio_highlights" in value) || Array.isArray(value.bio_highlights))
  );
}

function isNamedCodeRecord(value: unknown): value is { code: string; name: string } {
  return isRecord(value) && typeof value.code === "string" && typeof value.name === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
